import { Row } from '@tanstack/react-table';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import { Button } from '@/components/ui/button.tsx';
import { CalendarIcon } from '@radix-ui/react-icons';
import { cn, isValidDate } from '@/utils/utils.ts';
import { useMemo, useState } from 'react';
import { Calendar } from '@/components/ui/calendar.tsx';
import {
  GetPlantDeviceParameterChartResponse,
  useGetPlantDeviceParameterChartQuery,
} from '@/features/plants/hooks/queries/[plantId]/use-get-plant-device-parameter-chart.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import {
  DeviceTypeCodes,
  DeviceTypeNames,
} from '@/features/devices/schemas/devices.schemas.ts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { getFieldMetadata } from '@/features/devices/schemas/devices.metadata.ts';
import { useCSVExport } from '@/hooks/use-csv-export.ts';
import { FileSpreadsheet, LoaderIcon } from 'lucide-react';
import { useGetPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant.ts';

type TDevice = {
  unit: string;
  fieldCode: number;
  deviceTypeCode: DeviceTypeCodes;
  aggregation: string;
  time: string;
  value: number;
  last24Hours: TDevice[];
};

export const DeviceMetricsTableRowSubComponent = ({
  row,
  plantId,
  deviceType,
  deviceSN,
}: {
  row: Row<TDevice>;
  plantId: string;
  deviceType: DeviceTypeNames;
  deviceSN?: string;
}) => {
  const today = new Date();
  const [date, setDate] = useState<Date>(today);

  const field = getFieldMetadata(deviceType, row.original.fieldCode);

  const { data, isLoading } = useGetPlantDeviceParameterChartQuery({
    plantId,
    deviceType,
    deviceSN,
    parameter: row.original.fieldCode,
    dateRange: {
      startDate: new Date(date.setHours(0, 0, 0, 0)),
      endDate: new Date(date.setHours(23, 59, 59, 999)),
    },
  });

  const lines =
    data && data?.length > 0 ? Object.keys(data[0]).filter((x) => x !== 'time') : [];

  const numberOfDevices = data?.length ? Object.keys(data?.[0]).length : 0;

  return (
    <div className="space-y-6 py-4">
      <div className="flex flex-wrap items-center gap-4">
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" className={cn('text-sm font-normal')}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? format(date, 'PPP') : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => d && setDate(d)}
              disabled={{ after: today }}
            />
          </PopoverContent>
        </Popover>
        <ExportCSVButton
          data={data}
          plantId={plantId}
          deviceSN={deviceSN}
          deviceType={deviceType}
          exportedTimestamp={date}
        />
      </div>
      {isLoading && (
        <div className="flex h-[200px] w-full items-center justify-center">
          <LoadingSpinner className="h-[80px] w-[80px]" />
        </div>
      )}
      {!isLoading && (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart
            data={data}
            margin={{
              top: 5,
              right: 0,
              left: -20,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              minTickGap={10}
              dataKey="time"
              tickFormatter={(time: string) => {
                return format(isValidDate(time) ? time : new Date(), 'HH:mm:ss');
              }}
            />
            <YAxis domain={['auto', 'auto']} />
            <Tooltip
              cursor={false}
              content={(props) => (
                <ChartTooltipComponent
                  {...props}
                  field={field}
                  numberOfDevices={numberOfDevices}
                />
              )}
              wrapperStyle={{ zIndex: 9999 }}
            />
            <Legend />
            {lines.map((x) => {
              return (
                <Line name={String(x)} key={x} dataKey={x} dot={false} strokeWidth={1.5} />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const ChartTooltipComponent = ({
  payload,
  field,
  numberOfDevices,
}: TooltipContentProps<ValueType, NameType> & {
  field:
    | {
        name: string;
        unit: string;
        decimals: number;
      }
    | undefined;
  numberOfDevices: number;
}) => {
  const time = payload?.[0]?.payload?.time;
  const formattedDate = time ? format(new Date(time), 'HH:mm') : '';

  return (
    <div className="z-[20000] flex flex-col border border-gray-300 bg-white p-4 text-base">
      <p>{formattedDate}</p>
      <div
        className={cn(
          'mt-2 grid grid-cols-2 gap-1 gap-x-4',
          numberOfDevices > 25
            ? 'grid-cols-5'
            : numberOfDevices > 20
              ? 'grid-cols-4'
              : numberOfDevices > 12
                ? 'grid-cols-3'
                : 'grid-cols-2'
        )}
      >
        {payload?.map(
          (p: { stroke?: string; name?: string | number; value?: number | string }) => (
            <p style={{ color: p.stroke }} key={p.name}>
              <span className="text-primary">{p.name}:</span>{' '}
              {Number(p?.value).toFixed(field?.decimals ?? 2)} {field?.unit}
            </p>
          )
        )}
      </div>
    </div>
  );
};

interface PlantDeviceDataPoint {
  time: string;

  [parameterName: string]: string | number;
}

function ExportCSVButton({
  plantId,
  deviceSN,
  deviceType,
  exportedTimestamp,
  data,
}: {
  plantId?: string;
  deviceSN?: string;
  deviceType?: DeviceTypeNames;
  exportedTimestamp: Date;
  data?: GetPlantDeviceParameterChartResponse;
}) {
  const { data: plant } = useGetPlantQuery({ plantId });

  let baseFileName = plant?.basicDetails.name || plantId;
  baseFileName = deviceSN ? `${baseFileName}/${deviceType}/sn-${deviceSN}` : baseFileName;

  const csvColumns = useMemo(() => {
    if (!data || data.length === 0) return [];

    const columns = [
      {
        key: 'time',
        header: 'Timestamp',
        accessor: (item: PlantDeviceDataPoint) => item.time,
        formatter: (value: number | string | Date) => new Date(value).toLocaleString(),
        type: 'date',
      },
    ];

    const parameterNames = new Set<string>();
    for (const dataPoint of data) {
      for (const key of Object.keys(dataPoint)) {
        if (key !== 'time') {
          parameterNames.add(key);
        }
      }
    }

    for (const parameterName of Array.from(parameterNames).sort()) {
      columns.push({
        key: parameterName,
        header: formatParameterName(parameterName),
        accessor: (item) => String(item[parameterName]),
        formatter: (value) => {
          if (value == null || !value) return '';

          return typeof value === 'number' ? value.toFixed(3) : String(value);
        },
        type: 'number',
      });
    }

    return columns;
  }, [data]);

  const { downloadCSV, isExporting } = useCSVExport({
    data,
    columns: csvColumns,
    filename: `EMS_${baseFileName || 'metric'}_${new Date(exportedTimestamp).toLocaleDateString('ro-RO')}`,
    dateFormat: 'dd-MM-yyyy',
    locale: 'ro-RO',
  });

  return (
    <Button
      onClick={downloadCSV}
      size="sm"
      variant="outline"
      className={cn('gap-2 text-sm font-normal')}
    >
      {isExporting ? (
        <LoaderIcon className="size-4 animate-spin text-gray-800" aria-hidden="true" />
      ) : (
        <FileSpreadsheet className="size-4" />
      )}
      {isExporting ? 'Exporting...' : 'Export as CSV'}
    </Button>
  );
}

function formatParameterName(parameterName: string): string {
  return parameterName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
